// Import necessary modules and types
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { getClerkToken, isClerkConfigured } from './clerkClient';
import { getSavedLang } from './helpers';
import { captureError } from './posthog';
import { normalizeDateRangeForGranularity } from './utilities';

// Declare global cordova variable for TypeScript
declare global {
  interface Window {
    cordova: any;
  }
}

// Access environment variables
const ENV = (import.meta as any).env;
const USE_LOCAL_API = ENV?.VITE_APP_USE_LOCAL_API === 'true';

// Determine if the app is running on a native platform (iOS or Android)
const isNative = Capacitor.isNativePlatform();

type ApiRouteConfig = {
  pattern: string;
  envVar: () => string | undefined;
  rewrite?: (path: string, baseUrl: string) => string;
};

const CUSTOM_AGGREGATOR_PREFIX = '/v1/kwick';
const CUSTOM_SERVICE_ROOT = '/kwick';
const CUSTOM_SERVICE_VERSION_ROOT = `${CUSTOM_SERVICE_ROOT}/v1`;

const getBasePathname = (baseUrl: string): string => {
  try {
    const url = new URL(baseUrl);
    const pathname = url.pathname || '/';
    return pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  } catch {
    return '/';
  }
};

const ensureRelative = (value: string): string =>
  value === '/' ? '' : ensureLeadingSlash(value);

const stripServiceRootIfPresent = (
  targetPath: string,
  basePath: string
): string => {
  if (
    basePath === CUSTOM_SERVICE_ROOT ||
    basePath.endsWith(`${CUSTOM_SERVICE_ROOT}`)
  ) {
    const stripped = targetPath.slice(CUSTOM_SERVICE_ROOT.length);
    return stripped ? ensureLeadingSlash(stripped) : '/';
  }
  return targetPath;
};

const rewriteCustomPath = (path: string, baseUrl: string): string => {
  const normalized = ensureLeadingSlash(path);

  const basePath = getBasePathname(baseUrl);

  const buildTarget = (suffix = '') =>
    `${CUSTOM_SERVICE_VERSION_ROOT}${ensureRelative(suffix)}`;

  if (
    normalized === CUSTOM_SERVICE_VERSION_ROOT ||
    normalized.startsWith(`${CUSTOM_SERVICE_VERSION_ROOT}/`)
  ) {
    return stripServiceRootIfPresent(normalized, basePath);
  }

  if (
    normalized === CUSTOM_SERVICE_ROOT ||
    normalized.startsWith(`${CUSTOM_SERVICE_ROOT}/`)
  ) {
    const remainder = normalized.slice(CUSTOM_SERVICE_ROOT.length);
    const target = buildTarget(remainder);
    return stripServiceRootIfPresent(target, basePath);
  }

  if (normalized === CUSTOM_AGGREGATOR_PREFIX) {
    return stripServiceRootIfPresent(CUSTOM_SERVICE_VERSION_ROOT, basePath);
  }

  if (normalized.startsWith(`${CUSTOM_AGGREGATOR_PREFIX}/`)) {
    const remainder = normalized.slice(CUSTOM_AGGREGATOR_PREFIX.length);
    const target = buildTarget(remainder);
    return stripServiceRootIfPresent(target, basePath);
  }

  if (normalized.startsWith('/v1/')) {
    const remainder = normalized.slice('/v1'.length);
    const target = buildTarget(remainder);
    return stripServiceRootIfPresent(target, basePath);
  }

  const target = buildTarget(normalized);
  return stripServiceRootIfPresent(target, basePath);
};

// API endpoint routing configuration
// Maps URL patterns to their corresponding environment variable base URLs
const API_ROUTE_CONFIG: ApiRouteConfig[] = [
  { pattern: '/hive/api/kpi', envVar: () => ENV.VITE_APP_KPI_BASE_URL },
  { pattern: '/community/kpi', envVar: () => ENV.VITE_APP_KPI_BASE_URL },
  {
    pattern: '/hive/api/catalog',
    envVar: () => ENV.VITE_APP_CATALOG_BASE_URL,
  },
  {
    pattern: '/community/catalog',
    envVar: () => ENV.VITE_APP_CATALOG_BASE_URL,
  },
  {
    pattern: '/custom',
    envVar: () => ENV.VITE_APP_CUSTOM_BASE_URL,
    rewrite: rewriteCustomPath,
  },
  {
    pattern: '/common',
    envVar: () => ENV.VITE_APP_CUSTOM_BASE_URL,
    // Preserve the /common prefix in the path
    rewrite: (path: string) => `/common${path}`,
  },
  {
    pattern: '/org',
    envVar: () => ENV.VITE_APP_CUSTOM_BASE_URL,
    // Preserve the /org prefix in the path
    rewrite: (path: string) => `/org${path}`,
  },
];

const normalizeBaseUrl = (value?: string): string =>
  value ? value.replace(/\/+$/, '') : '';

const ensureLeadingSlash = (value: string): string =>
  value.startsWith('/') ? value : `/${value}`;

/**
 * When running locally we want env-provided URLs to include the full gateway prefix
 * (e.g. http://localhost:18080/community/catalog). The frontend still calls the
 * canonical `/community/...` paths, so we strip that prefix from the request path
 * and append the remainder to the custom base.
 */
const rewriteUrlForLocalRouting = (pathAndQuery: string): string | null => {
  if (!USE_LOCAL_API) return null;

  for (const route of API_ROUTE_CONFIG) {
    const customBaseUrl = normalizeBaseUrl(route.envVar());
    if (!customBaseUrl) continue;

    if (pathAndQuery.startsWith(route.pattern)) {
      const remainingPath = pathAndQuery.slice(route.pattern.length) || '/';
      const rawPath = ensureLeadingSlash(remainingPath);
      const normalizedPath = route.rewrite
        ? route.rewrite(rawPath, customBaseUrl)
        : rawPath;
      const rewritten = `${customBaseUrl}${ensureLeadingSlash(normalizedPath)}`;
      return rewritten.replace(/([^:]\/)\/+/g, '$1');
    }
  }

  return null;
};

// Helper function to construct the full URL with the appropriate base URL
const constructFullUrl = (url: string): string => {
  const normalizeFallbackUrl = (pathAndQuery: string): string => {
    const baseUrl = normalizeBaseUrl(ENV.VITE_APP_BASE_URL);
    return `${baseUrl}${ensureLeadingSlash(pathAndQuery)}`.replace(
      /([^:]\/)\/+/g,
      '$1'
    );
  };

  if (url.startsWith('/kwick')) {
    return url;
  }

  // If the URL already contains a base URL (full URL), extract the path and reconstruct
  if (url.startsWith('http')) {
    try {
      const urlObj = new URL(url);
      const pathAndQuery = (
        urlObj.pathname +
        urlObj.search +
        urlObj.hash
      ).replace(/\/\/+/g, '/');
      const localRewrite = rewriteUrlForLocalRouting(pathAndQuery);
      return localRewrite ?? normalizeFallbackUrl(pathAndQuery);
    } catch (e) {
      // If URL parsing fails, return the original URL
      console.error('Error parsing URL:', url, e);
      return url;
    }
  }

  const pathWithLeadingSlash = ensureLeadingSlash(url);
  const localRewrite = rewriteUrlForLocalRouting(pathWithLeadingSlash);
  if (localRewrite) {
    return localRewrite;
  }

  return normalizeFallbackUrl(pathWithLeadingSlash);
};

// Create an Axios instance for the web platform with the base URL
const api = axios.create({
  baseURL: ENV.VITE_APP_BASE_URL,
});

// Request interceptor for setting headers in Axios
api.interceptors.request.use(
  async (config) => {
    // Legacy: If an idToken is provided, set the Authorization header (for backward compatibility)
    if (config.headers.idToken) {
      config.headers.Authorization = `Bearer ${config.headers.idToken}`;
      delete config.headers.idToken; // Remove idToken after setting Authorization
      return config;
    }

    // Auto-inject Clerk token if configured and no Authorization header exists
    if (isClerkConfigured() && !config.headers.Authorization) {
      try {
        const token = await getClerkToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('[httpService] Failed to get Clerk token:', error);
        // Don't fail the request, let it proceed without token
        // The backend will return 401 if authentication is required
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for centralized error handling in Axios
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Enhanced error handling
    if (error.response) {
      // The request was made, and the server responded with a status code outside of 2xx
      // console.error('Response Data:', error.response.data);
      // console.error('Status Code:', error.response.status);
      // console.error('Headers:', error.response.headers);

      // PostHog: only report genuine server-side failures (5xx). Expected 4xx
      // client errors — notably 401 (token expiry) and 403 (scoping) — are normal
      // control flow, not bugs. Sending every 4xx here flooded error tracking with
      // AxiosError "$exception" events that PostHog cannot even fingerprint, so we
      // skip them and keep only real server errors. Client-side 4xx stay visible
      // server-side (API request logs + Loki).
      if (error.response.status >= 500) {
        captureError(error, {
          type: 'api_response_error',
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          status: error.response.status,
          statusText: error.response.statusText,
          responseData: error.response.data,
        });
      }

      // Handle 401 Unauthorized errors (token expired or invalid)
      if (error.response.status === 401 && isClerkConfigured()) {
        // Only dispatch clerk-token-expired if we actually sent a token
        // This prevents logout loops when Clerk cookies fail (no token was attached)
        const hadAuthHeader = error.config?.headers?.Authorization;
        // Skip logout for requests that use 401 to mean "wrong password" (e.g. password-change)
        const skipLogout = (error.config as any)?.skipLogoutOn401 === true;

        // Only trigger logout if we actually sent a token (indicates token expired/invalid)
        if (hadAuthHeader && !skipLogout) {
          const tokenExpiredEvent = new CustomEvent('clerk-token-expired');
          window.dispatchEvent(tokenExpiredEvent);
        }
      }
    } else if (error.request) {
      // The request was made, but no response was received
      // PostHog: Capture network/timeout errors
      captureError(error, {
        type: 'api_no_response',
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        message: 'No response received from server',
      });
    } else {
      // An error occurred in setting up the request
      // PostHog: Capture request setup errors
      captureError(error, {
        type: 'api_request_error',
        message: error.message,
      });
    }
    return Promise.reject(error);
  }
);

// Define the interface for request options
interface RequestOptions {
  url: string;
  data?: any;
  params?: any;
  idToken?: string; // Deprecated: kept for backward compatibility, will be removed
  additionalHeaders?: any;
  mock?: boolean;
  skipLogoutOn401?: boolean; // When true, a 401 response will NOT trigger the clerk-token-expired logout event
}

// Cache for recent requests to prevent duplicates
const requestCache: Record<
  string,
  { timestamp: number; data?: any; response?: any }
> = {};
const CACHE_EXPIRY = 5000; // Cache expiry time in milliseconds (1 second)

// Utility function to create a unique cache key based on URL and data
const getCacheKey = (url: string, data?: any): string => {
  return `${url}:${JSON.stringify(data)}`;
};

// Function to check if a request is a duplicate within the cache expiry time
const isDuplicateRequest = (url: string, data?: any): boolean => {
  const cacheKey = getCacheKey(url, data);
  const cachedRequest = requestCache[cacheKey];

  if (cachedRequest) {
    const currentTime = Date.now();
    if (currentTime - cachedRequest.timestamp < CACHE_EXPIRY) {
      return true; // Duplicate request detected within the time window
    }
  }
  return false;
};

// Function to store a request in the cache
const cacheRequest = (url: string, data?: any, response?: any): void => {
  const cacheKey = getCacheKey(url, data);
  requestCache[cacheKey] = { timestamp: Date.now(), data, response };
};

// The main httpService object containing methods for GET, POST, PUT, and DELETE
export const httpService = {
  /**
   * GET request method
   * @param {RequestOptions} options - The request options
   * @returns {Promise<any>} - The response from the server
   */
  get: async ({ url, idToken, params, mock }: RequestOptions): Promise<any> => {
    // Check for duplicate requests

    const savedLang = getSavedLang() || null;
    const browserLanguage =
      savedLang?.split('_')[1] ||
      navigator.language.split('-')[0] ||
      ENV.VITE_APP_LANGUAGE;

    const _params = {
      ...params,
      locale: browserLanguage,
    };

    if (isDuplicateRequest(url, _params)) {
      // console.warn(`Duplicate GET request to ${url} prevented.`);
      return requestCache[getCacheKey(url, _params)].response as any;
    }

    // Construct the full URL with the appropriate base URL
    const fullUrl = constructFullUrl(url);

    const getMock = () => {
      if (mock) return 'true';
      else return ENV?.VITE_APP_MOCK === 'true' ? 'true' : 'false';
    };

    // Set up headers
    const headersBase = {
      mock: getMock(),
    } as const;

    const headers = idToken
      ? { ...headersBase, Authorization: `Bearer ${idToken}` }
      : headersBase;

    try {
      // Normalize date range for month/year granularity if present
      const normalizedParams = normalizeDateRangeForGranularity(_params || {});
      let response;

      if (
        isNative &&
        window.cordova &&
        window.cordova.plugin &&
        window.cordova.plugin.http
      ) {
        // Native platform using cordova-plugin-advanced-http with SSL pinning
        response = await new Promise((resolve, reject) => {
          window.cordova.plugin.http.get(
            fullUrl,
            normalizedParams || {},
            headers,
            (res: any) => {
              // Success callback
              if (res.data && typeof res.data === 'string') {
                try {
                  res.data = JSON.parse(res.data);
                } catch (e) {
                  // console.error('Error parsing response data:', e);
                }
              }
              resolve(res);
            },
            (err: any) => {
              // Error callback
              reject(err);
            }
          );
        });
      } else {
        // Web platform using Axios
        response = await api.get(fullUrl, {
          headers,
          params: normalizedParams,
        });
      }

      // Cache the request after a successful response
      cacheRequest(url, normalizedParams, response);

      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  /**
   * POST request method
   * @param {RequestOptions} options - The request options
   * @returns {Promise<any>} - The response from the server
   */
  post: async ({
    url,
    data,
    idToken,
    additionalHeaders,
    skipLogoutOn401,
  }: RequestOptions): Promise<any> => {
    // Check for duplicate requests

    const savedLang = getSavedLang() || null;
    const browserLanguage =
      savedLang?.split('_')[1] ||
      navigator.language.split('-')[0] ||
      ENV.VITE_APP_LANGUAGE;
    const _data = {
      locale: browserLanguage,
      ...data,
    };
    if (isDuplicateRequest(url, _data)) {
      // console.warn(`Duplicate GET request to ${url} prevented.`);
      return requestCache[getCacheKey(url, _data)].response as any;
    }

    // Construct the full URL with the appropriate base URL
    const fullUrl = constructFullUrl(url);

    // Set up headers
    const headersBase = {
      'Content-Type': 'application/json',
      mock: ENV?.VITE_APP_MOCK === 'true' ? 'true' : 'false',
      ...additionalHeaders,
    } as const;

    const headers = idToken
      ? { ...headersBase, Authorization: `Bearer ${idToken}` }
      : headersBase;

    try {
      // Normalize date range for month/year granularity if present
      const normalizedData = normalizeDateRangeForGranularity(_data || {});
      let response;

      if (
        isNative &&
        window.cordova &&
        window.cordova.plugin &&
        window.cordova.plugin.http
      ) {
        // Native platform using cordova-plugin-advanced-http with SSL pinning
        response = await new Promise((resolve, reject) => {
          window.cordova.plugin.http.post(
            fullUrl,
            normalizedData || {},
            headers,
            (res: any) => {
              // Success callback
              if (res.data && typeof res.data === 'string') {
                try {
                  res.data = JSON.parse(res.data);
                } catch (e) {
                  alert('here');
                  // console.error('Error parsing response data:', e);
                }
              }
              resolve(res);
            },
            (err: any) => {
              alert('there');
              // Error callback
              reject(err);
            }
          );
        });
      } else {
        // Web platform using Axios
        response = await api.post(fullUrl, normalizedData, {
          headers,
          skipLogoutOn401,
        } as any);
      }

      // Cache the request after a successful response

      cacheRequest(url, normalizedData, response);

      return response;
    } catch (error: any) {
      console.error(error);
      if (error.response.status === 503 || error.response.status === 504) {
        return {
          status: 504,
          statusText: 'Gateway Timeout/Error',
        } as any;
      }
      if (error.response.status === 400) {
        return {
          status: 400,
          statusText: 'Bad Request',
        } as any;
      }
      if (error.response.status === 401) {
        return {
          status: 401,
          statusText: 'Unauthorized',
        } as any;
      }
      if (error.response.status === 404) {
        return {
          status: 404,
          statusText: 'Not Found',
        } as any;
      }

      throw error;
    }
  },

  /**
   * PUT request method
   * @param {RequestOptions} options - The request options
   * @returns {Promise<any>} - The response from the server
   */
  put: async ({ url, data, idToken }: RequestOptions): Promise<any> => {
    // Check for duplicate requests
    if (isDuplicateRequest(url, data)) {
      // console.warn(`Duplicate PUT request to ${url} prevented.`);
      return {
        status: 999,
        statusText: 'OK',
      } as any;
    }

    // Construct the full URL with the appropriate base URL
    const fullUrl = constructFullUrl(url);

    // Set up headers
    const headersBase = {
      'Content-Type': 'application/json',
      mock: ENV?.VITE_APP_MOCK === 'true' ? 'true' : 'false',
    } as const;

    const headers = idToken
      ? { ...headersBase, Authorization: `Bearer ${idToken}` }
      : headersBase;

    try {
      let response;

      if (
        isNative &&
        window.cordova &&
        window.cordova.plugin &&
        window.cordova.plugin.http
      ) {
        // Native platform using cordova-plugin-advanced-http with SSL pinning
        response = await new Promise((resolve, reject) => {
          window.cordova.plugin.http.put(
            fullUrl,
            data || {},
            headers,
            (res: any) => {
              // Success callback
              if (res.data && typeof res.data === 'string') {
                try {
                  res.data = JSON.parse(res.data);
                } catch (e) {
                  // console.error('Error parsing response data:', e);
                }
              }
              resolve(res);
            },
            (err: any) => {
              // Error callback
              reject(err);
            }
          );
        });
      } else {
        // Web platform using Axios
        response = await api.put(fullUrl, data, {
          headers,
        });
      }

      // Cache the request after a successful response
      cacheRequest(url, data, response);

      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  /**
   * PATCH request method
   * @param {RequestOptions} options - The request options
   * @returns {Promise<any>} - The response from the server
   */
  patch: async ({
    url,
    data,
    idToken,
    mock,
    skipLogoutOn401,
  }: RequestOptions): Promise<any> => {
    // Check for duplicate requests
    if (isDuplicateRequest(url, data)) {
      return {
        status: 999,
        statusText: 'OK',
      } as any;
    }

    // Construct the full URL with the appropriate base URL
    const fullUrl = constructFullUrl(url);

    const getMock = () => {
      if (mock) return 'true';
      else return ENV?.VITE_APP_MOCK === 'true' ? 'true' : 'false';
    };

    // Set up headers
    const headersBase = {
      'Content-Type': 'application/json',
      mock: getMock(),
    } as const;

    const headers = idToken
      ? { ...headersBase, Authorization: `Bearer ${idToken}` }
      : headersBase;

    try {
      let response;

      if (
        isNative &&
        window.cordova &&
        window.cordova.plugin &&
        window.cordova.plugin.http
      ) {
        // Native platform using cordova-plugin-advanced-http with SSL pinning
        response = await new Promise((resolve, reject) => {
          window.cordova.plugin.http.patch(
            fullUrl,
            data || {},
            headers,
            (res: any) => {
              // Success callback
              if (res.data && typeof res.data === 'string') {
                try {
                  res.data = JSON.parse(res.data);
                } catch (e) {
                  // console.error('Error parsing response data:', e);
                }
              }
              resolve(res);
            },
            (err: any) => {
              // Error callback
              reject(err);
            }
          );
        });
      } else {
        // Web platform using Axios
        response = await api.patch(fullUrl, data, {
          headers,
          skipLogoutOn401,
        } as any);
      }

      // Cache the request after a successful response
      cacheRequest(url, data, response);

      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  /**
   * DELETE request method
   * @param {RequestOptions} options - The request options
   * @returns {Promise<any>} - The response from the server
   */
  delete: async ({ url, data, idToken }: RequestOptions): Promise<any> => {
    // Check for duplicate requests
    if (isDuplicateRequest(url)) {
      // console.warn(`Duplicate DELETE request to ${url} prevented.`);
      return {
        status: 999,
        statusText: 'OK',
      } as any;
    }

    // Construct the full URL with the appropriate base URL
    const fullUrl = constructFullUrl(url);

    // Set up headers
    const headersBase = {
      mock: ENV?.VITE_APP_MOCK === 'true' ? 'true' : 'false',
    } as const;

    const headers = idToken
      ? { ...headersBase, Authorization: `Bearer ${idToken}` }
      : headersBase;

    try {
      let response;

      if (
        isNative &&
        window.cordova &&
        window.cordova.plugin &&
        window.cordova.plugin.http
      ) {
        // Native platform using cordova-plugin-advanced-http with SSL pinning
        response = await new Promise((resolve, reject) => {
          window.cordova.plugin.http.delete(
            fullUrl,
            data || {},
            headers,
            (res: any) => {
              // Success callback
              if (res.data && typeof res.data === 'string') {
                try {
                  res.data = JSON.parse(res.data);
                } catch (e) {
                  // console.error('Error parsing response data:', e);
                }
              }
              resolve(res);
            },
            (err: any) => {
              // Error callback
              reject(err);
            }
          );
        });
      } else {
        // Web platform using Axios
        response = await api.delete(fullUrl, {
          headers,
          data: data || {},
        });
      }

      // Cache the request after a successful response
      cacheRequest(url);

      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
};

export default httpService;
